import { isTimeExpired } from './helpers.js';
import { CircuitBreakerState } from './types.js';
export class CircuitBreaker {
    config;
    state = CircuitBreakerState.CLOSED;
    /**
     * Массив логов запросов. Хранит в себе {minimumSamples} последних запросов.
     * Время хранения запроса {samplingDuration}
     *
     * Четное значение - это время
     * Нечетное значение - статус success/fail
     */
    samples = [];
    /**
     * Время последнего открытия CB
     */
    lastCBOpenTime;
    /**
     * Время, когда CB был полуоткрыт.
     */
    lastHalfOpenTime;
    lastHalfOpenTaskIndex = 0;
    constructor(config) {
        this.config = Object.assign({}, config);
    }
    setConfig(nextConfig) {
        this.config = Object.assign(this.config, nextConfig);
    }
    /**
     * Метод, который в зависимости от состояния говорит, можно ли запускать задачу.
     *
     * Если состояние OPEN:
     * - Проверяем истекло ли время {openDuration}
     * - Если время истекло, то переводим в состояние HALF_OPEN разрешаем выполнить одну(!) задачу
     * - Если время не истекло, то запрещаем выполнять задачу
     *
     * Если состояния HALF_OPEN:
     * - Проверяем истекло ли время пробной задачи
     * - Если нет, то запрещаем выполнение следующей задачи
     * - Если да, то пытаемся закрыть CB
     *
     * Если состояние CLOSE:
     * - Анализируем колекцию сэмплов
     * - Если допустимый процент неудачных задач превышен, то "открываем" CB и запрещаем выполнение задачи
     * - Если попрог не превышен, то позволяем выполнить задачу
     */
    canRunTask() {
        const { maxTimeForHalfOpenRequest, openDuration } = this.config;
        if (this.state === CircuitBreakerState.OPEN) {
            if (!(this.lastCBOpenTime == null) && isTimeExpired(this.lastCBOpenTime, openDuration)) {
                this.halfOpen();
                return true;
            }
            return false;
        }
        if (this.state === CircuitBreakerState.HALF_OPEN) {
            // С начало смотрим, выполнилась ли наша задача. Если нет, то смотрим, истекло ли время на задачу.
            if (this.analyzeLastHalfOpenTask() ||
                (this.lastHalfOpenTime && isTimeExpired(this.lastHalfOpenTime, maxTimeForHalfOpenRequest))) {
                return this.tryCloseCircuitBreaker();
            }
            return false;
        }
        const failureThresholdExceeded = this.failureThresholdExceeded();
        if (failureThresholdExceeded) {
            this.open();
            return false;
        }
        return true;
    }
    recordSuccess() {
        this.record(true);
    }
    recordFailure() {
        this.record(false);
    }
    setState(state) {
        this.state = state;
        return state;
    }
    /**
     * Переводим CB в состояние полуоткрыт. Это позволяет нам отправить 1 запрос.
     */
    halfOpen() {
        this.lastHalfOpenTime = Date.now();
        this.setState(CircuitBreakerState.HALF_OPEN);
    }
    /**
     * Открытие предохранителя.
     * Когда мы открываем предохранитель, то очищаем время последнего полуоткрытого состояния.
     */
    open() {
        // Очищаем коллекцию сэмплов, потому что при открытом состоянии мы не собираем результаты задач
        // и это нам гарантирует, что когда мы перейдем в состояние "полу открыт", у нас будет только 1 задача
        this.clearSamples();
        this.lastHalfOpenTime = undefined;
        this.lastCBOpenTime = Date.now();
        this.setState(CircuitBreakerState.OPEN);
    }
    /**
     * Закрытие CB. Когда мы закрываем CB, то мы должны очистить все таймеры.
     */
    close() {
        this.lastCBOpenTime = undefined;
        this.lastHalfOpenTime = undefined;
        this.setState(CircuitBreakerState.CLOSED);
    }
    /**
     * Попытка закрыть CB из состояния HALF_OPEN. Анализируем последнюю задачу:
     * - Если она успешна, то закрываем, если нет то открываем.
     */
    tryCloseCircuitBreaker() {
        if (this.analyzeLastHalfOpenTask()) {
            this.close();
            return true;
        }
        this.open();
        return false;
    }
    analyzeLastHalfOpenTask() {
        if (!this.samples.length) {
            return false;
        }
        return this.samples[this.lastHalfOpenTaskIndex + 1] || false;
    }
    record(success) {
        const { maximumSamples } = this.config;
        this.clearExpiredSamples();
        if (this.samplesTotal >= maximumSamples) {
            this.samples = this.samples.slice(2);
        }
        this.samples.push(Date.now(), success);
    }
    /**
     * Проверяем, превышен ли порог допустимых фэйлов
     */
    failureThresholdExceeded() {
        if (!this.samples.length) {
            return false;
        }
        if (this.samplesTotal < this.config.minimumSamples) {
            return false;
        }
        const { failureTreshold } = this.config;
        // Количество сфэйленных запросов
        const failureSamples = this.samples.reduce((count, success) => (typeof success === 'boolean' && !success ? count + 1 : count), 0);
        // Сравниваем процент сфэйленных запросов с допустимым количеством
        return failureSamples / this.samplesTotal >= failureTreshold;
    }
    clearExpiredSamples() {
        if (!this.samples.length) {
            return;
        }
        const { samplingDuration } = this.config;
        const result = [];
        for (let i = 0; i < this.samples.length; i += 2) {
            const date = this.samples[i];
            const success = this.samples[i + 1];
            if (!isTimeExpired(date, samplingDuration)) {
                result.push(date, success);
            }
        }
        this.samples = result;
    }
    clearSamples() {
        this.samples = [];
    }
    get samplesTotal() {
        return this.samples.length / 2;
    }
}
//# sourceMappingURL=circuitBreaker.js.map